import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Grid,
    Pagination,
    Avatar,
    Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SCUIContent } from '../../../atomic/organisms';
import RoleApprovalQueryService from '../../../services/query/roleApproval/RoleApprovalQuery.services';
import Swal from 'sweetalert2';
import { RequestNoteModal } from './components/RequestNoteModal';
import { RequestDetailModal } from './components/RequestDetailModal';
import { MyRoleRequest, MyRoleRequestRow } from './MyRoleRequestRow';
import { formatMyRequestDate } from './myRoleRequests.helpers';

interface ActiveRequestsResponse {
    items: MyRoleRequest[];
}

interface PastRequestsResponse {
    items: MyRoleRequest[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
}

export const MyRoleRequests: FC = () => {
    const { t } = useTranslation();
    const [activeRequests, setActiveRequests] = useState<MyRoleRequest[]>([]);
    const [pastRequests, setPastRequests] = useState<MyRoleRequest[]>([]);
    const [loadingActive, setLoadingActive] = useState(false);
    const [loadingPast, setLoadingPast] = useState(false);
    const [pastRequestsExpanded, setPastRequestsExpanded] = useState(false);
    const [pastRequestsLoaded, setPastRequestsLoaded] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;

    // Modal state - request note (active requests)
    const [selectedRequest, setSelectedRequest] = useState<MyRoleRequest | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Modal state - request detail (past requests)
    const [detailRequest, setDetailRequest] = useState<MyRoleRequest | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const handleOpenModal = (request: MyRoleRequest) => {
        setSelectedRequest(request);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRequest(null);
    };

    const handleOpenDetailModal = (request: MyRoleRequest) => {
        setDetailRequest(request);
        setIsDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setDetailRequest(null);
    };

    useEffect(() => {
        fetchActiveRequests();
    }, []);

    const fetchActiveRequests = async () => {
        setLoadingActive(true);
        try {
            const response: ActiveRequestsResponse = await RoleApprovalQueryService.GetMyActiveRequests();
            if (response && response.items) {
                setActiveRequests(response.items);
            }
        } catch (error) {
            console.error('Error fetching active requests:', error);
            Swal.fire(t('general.error'), t('roleApproval.errorFetchingRequests'), 'error');
        } finally {
            setLoadingActive(false);
        }
    };

    const fetchPastRequests = async (page: number = 1) => {
        setLoadingPast(true);
        try {
            const response: PastRequestsResponse = await RoleApprovalQueryService.GetMyPastRequests({
                PageNumber: page,
                PageSize: pageSize,
            });

            if (response && response.items) {
                setPastRequests(response.items);
                setTotalPages(response.totalPages);
                setTotalCount(response.totalCount);
                setCurrentPage(response.currentPage);
                setPastRequestsLoaded(true);
            }
        } catch (error) {
            console.error('Error fetching past requests:', error);
            Swal.fire(t('general.error'), t('roleApproval.errorFetchingRequests'), 'error');
        } finally {
            setLoadingPast(false);
        }
    };

    const handlePastRequestsAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
        setPastRequestsExpanded(isExpanded);
        if (isExpanded && !pastRequestsLoaded) {
            fetchPastRequests(1);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        fetchPastRequests(page);
    };

    const renderRequestRow = (request: MyRoleRequest) => (
        <MyRoleRequestRow
            key={request.requestId}
            request={request}
            onOpenNote={handleOpenModal}
            onOpenDetail={handleOpenDetailModal}
        />
    );

    return (
        <>
            <SCUIContent>
                <Box
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        maxWidth: '1400px',
                        margin: '0 auto',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                                color: '#1E293B',
                            }}
                        >
                            {t('roleApproval.myRoleRequests.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: '#64748B' }}>
                            {t('roleApproval.myRoleRequests.description')}
                        </Typography>
                    </Box>

                    {/* Active Requests Accordion */}
                    <Accordion
                        defaultExpanded
                        sx={{
                            mb: 3,
                            borderRadius: '12px !important',
                            overflow: 'hidden',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={{
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #E2E8F0',
                                minHeight: 72,
                                '&:hover': {
                                    bgcolor: '#F1F5F9',
                                },
                                '& .MuiAccordionSummary-content': {
                                    my: 2,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '100%',
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: '#5B7A9F',
                                        width: 40,
                                        height: 40,
                                    }}
                                >
                                    <AccessTimeIcon />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: '#1E293B' }}>
                                        {t('roleApproval.myRoleRequests.activeRequests')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                        {t('roleApproval.myRoleRequests.activeRequestsDescription')}
                                    </Typography>
                                </Box>
                                {activeRequests.length > 0 && (
                                    <Chip
                                        label={activeRequests.length}
                                        sx={{
                                            bgcolor: '#E3F2FD',
                                            color: '#1565C0',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            height: 28,
                                            minWidth: 36,
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            {loadingActive ? (
                                <Box sx={{ textAlign: 'center', p: 6 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('roleApproval.myRoleRequests.loading')}
                                    </Typography>
                                </Box>
                            ) : activeRequests.length === 0 ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 6,
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {t('roleApproval.myRoleRequests.noActiveRequests')}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box>
                                    {/* Table Header */}
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            bgcolor: '#F8FAFC',
                                            borderRadius: '8px',
                                            display: { xs: 'none', md: 'block' },
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid
                                                size={{
                                                    md: 3,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.requestedRole')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.status')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 3,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.approver')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.requestDate')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                                sx={{ textAlign: 'center' }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.actions')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {activeRequests.map((request) => renderRequestRow(request))}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Past Requests Accordion */}
                    <Accordion
                        expanded={pastRequestsExpanded}
                        onChange={handlePastRequestsAccordionChange}
                        sx={{
                            borderRadius: '12px !important',
                            overflow: 'hidden',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#475569' }} />}
                            sx={{
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #E2E8F0',
                                minHeight: 72,
                                '&:hover': {
                                    bgcolor: '#F1F5F9',
                                },
                                '& .MuiAccordionSummary-content': {
                                    my: 2,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '100%',
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: '#64748B',
                                        width: 40,
                                        height: 40,
                                    }}
                                >
                                    <CheckCircleIcon />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: '#1E293B' }}>
                                        {t('roleApproval.myRoleRequests.pastRequests')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                        {t('roleApproval.myRoleRequests.pastRequestsDescription')}
                                    </Typography>
                                </Box>
                                {totalCount > 0 && (
                                    <Chip
                                        label={totalCount}
                                        sx={{
                                            bgcolor: '#F1F5F9',
                                            color: '#475569',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            height: 28,
                                            minWidth: 36,
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FAFBFC' }}>
                            {loadingPast ? (
                                <Box sx={{ textAlign: 'center', p: 6 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        {t('roleApproval.myRoleRequests.loadingPastRequests')}
                                    </Typography>
                                </Box>
                            ) : pastRequests.length === 0 ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        textAlign: 'center',
                                        p: 6,
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {t('roleApproval.myRoleRequests.noPastRequests')}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box>
                                    {/* Table Header */}
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            bgcolor: '#F8FAFC',
                                            borderRadius: '8px',
                                            display: { xs: 'none', md: 'block' },
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid
                                                size={{
                                                    md: 3,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.requestedRole')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.status')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 3,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.approver')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.requestDate')}
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                size={{
                                                    md: 2,
                                                }}
                                                sx={{ textAlign: 'center' }}
                                            >
                                                <Typography variant="caption" fontWeight={600} color="#64748B">
                                                    {t('roleApproval.myRoleRequests.actions')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {pastRequests.map((request) => renderRequestRow(request))}
                                    {totalPages > 1 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mt: 4,
                                                pt: 3,
                                                borderTop: '2px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={handlePageChange}
                                                color="primary"
                                                showFirstButton
                                                showLastButton
                                                size="large"
                                                sx={{
                                                    '& .MuiPaginationItem-root': {
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </SCUIContent>

            {/* Request Note Modal (active requests) */}
            {selectedRequest && (
                <RequestNoteModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    requestNote={selectedRequest.requestNote}
                    roleName={selectedRequest.requestedRoleName}
                    requestDate={formatMyRequestDate(selectedRequest.requestDate)}
                />
            )}

            {/* Request Detail Modal (past requests) */}
            <RequestDetailModal
                open={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                request={detailRequest}
                formatDate={formatMyRequestDate}
            />
        </>
    );
};
